import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Music2, Users, GraduationCap, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';

const LS_KEY = 'rc_free_tier_dismissed_v1';

const UNLOCKS = [
  {
    id: 'reel',
    title: 'Music Reel',
    desc: 'Swipe through indie artist tracks & music videos — no account needed to preview.',
    cta: 'Open the reel',
    to: '/feed',
    Icon: Music2,
    color: '#22d3ee',
    tag: 'Free',
  },
  {
    id: 'artists',
    title: 'Artist directory',
    desc: 'Follow indie artists and build your reel — 100% free forever.',
    cta: 'Browse artists',
    to: '/artists',
    Icon: Users,
    color: '#a78bfa',
    tag: 'Free',
  },
  {
    id: 'academy',
    title: 'Music Biz Academy',
    desc: 'Start with Copyright Foundations — free sample lesson unlocks your first AI Specialist.',
    cta: 'Try a lesson',
    to: '/academy',
    Icon: GraduationCap,
    color: '#ffcf52',
    tag: 'Free sample',
  },
  {
    id: 'hub',
    title: 'Knowledge Hub',
    desc: 'Stream free podcast episodes on royalties, publishing, and sync licensing.',
    cta: 'Open the Hub',
    to: '/hub',
    Icon: BookOpen,
    color: '#f472b6',
    tag: 'Free podcasts',
  },
];

export default function FreeTierPanel() {
  const { user } = useApp();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(LS_KEY) === '1');
  }, []);

  // Show to anonymous users and free-tier musicians/listeners — hide for paid tiers.
  const tier = (user?.reapercast_tier || 'free').toLowerCase();
  const isPaid = ['starter', 'pro', 'label'].includes(tier);
  if (dismissed || isPaid) return null;

  const dismiss = () => { localStorage.setItem(LS_KEY, '1'); setDismissed(true); };

  return (
    <section
      className="relative rounded-2xl overflow-hidden mb-8 border"
      style={{
        borderColor: 'rgba(167,139,250,0.35)',
        background: 'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(34,211,238,0.08) 45%, rgba(255,207,82,0.06))',
      }}
      data-testid="rc-free-tier-panel"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss free-tier panel"
        className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-full bg-black/30 hover:bg-black/50 text-white/70 hover:text-white"
        data-testid="rc-free-tier-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-5 md:p-7">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg grid place-items-center bg-white/10">
            <Sparkles className="w-4 h-4 text-[#ffcf52]" />
          </span>
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#ffcf52]">Your free unlocks</span>
        </div>
        <h2 className="font-display text-[22px] md:text-[28px] leading-tight max-w-[620px]">
          {user ? `Welcome back${user.name ? ', ' + user.name.split(' ')[0] : ''} — you've got 4 free unlocks waiting.` : "You've got 4 free unlocks waiting."}
        </h2>
        <p className="text-white/70 text-[13px] md:text-[14px] mt-1 max-w-[600px]">
          No paywall to cross — try the listener reel, browse indie artists, take a free Academy lesson, and stream a music-business podcast.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {UNLOCKS.map(u => {
            const Ic = u.Icon;
            return (
              <Link
                key={u.id}
                to={u.to}
                className="group rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] p-4 transition-colors flex flex-col"
                data-testid={`rc-free-tier-${u.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: u.color + '22', color: u.color }}>
                    <Ic className="w-4 h-4" />
                  </span>
                  <span className="chip text-[10px]" style={{ color: u.color, borderColor: u.color + '55' }}>{u.tag}</span>
                </div>
                <div className="font-semibold text-[14px]">{u.title}</div>
                <p className="text-[12px] text-white/60 leading-relaxed mt-1 flex-1">{u.desc}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: u.color }}>
                  {u.cta} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
